import { differenceInYears, isAfter, isValid, parse } from "date-fns";
import { z } from "zod";
import { isValidPostalCode } from "../../utils/validationUtils";

// Enhanced date of birth validation function
const validateDateOfBirth = (value: string) => {
  // Basic format validation (MM/DD/YYYY)
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    return false;
  }

  const dateParts = value.split("/");
  if (dateParts.length !== 3) return false;

  const month = parseInt(dateParts[0], 10);
  const day = parseInt(dateParts[1], 10);
  const year = parseInt(dateParts[2], 10);

  // Validate basic ranges
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    year === 0 ||
    year > 9999
  ) {
    return false;
  }

  // Parse the date - this will validate days against months (e.g., no Feb 30)
  const parsedDate = parse(value, "MM/dd/yyyy", new Date());

  // Check if date is valid
  if (!isValid(parsedDate)) {
    return false;
  }

  // Check if date is in the future
  if (isAfter(parsedDate, new Date())) {
    return false;
  }

  // Check if age is reasonable (not over 120 years)
  const age = differenceInYears(new Date(), parsedDate);
  return age <= 120;
};

// Can't chain `extend` after `refine` in Zod, so we need to call `refine` twice :/
// Extracted here to deduplicate as much as possible
// https://github.com/colinhacks/zod/issues/454
const refinePostalCode: Parameters<(typeof sharedContactFormSchema)["refine"]> =
  [
    (data) => isValidPostalCode(data.zipCode, data.country),
    {
      message: "Please enter a valid postal code for your country",
      path: ["zipCode"], // Path indicates which field has the error
    },
  ];

// Phone validation refinement that checks the boolean state from component
const refinePhoneValidation: Parameters<
  (typeof sharedContactFormSchema)["refine"]
> = [
  (data) => data.phoneValid,
  {
    message: "Please enter a valid phone number",
    path: ["phone"], // Show error on phone field, not phoneValid
  },
];

// more refinement - perhaps .superRefine would let us handle all of this at once?
const refineThirdPartyContactConsent: Parameters<
  (typeof sharedContactFormSchema)["refine"]
> = [
  (data) => {
    if (data.thirdPartyContactConsentVersion !== "") {
      return data.thirdPartyContactConsentGranted;
    }
    return true;
  },
  {
    message: "You must consent to being contacted about this study",
    path: ["thirdPartyContactConsentGranted"],
  },
];

const sharedContactFormSchema = z.object({
  country: z.string(), // Country code (e.g., 'US', 'CA')
  zipCode: z.string().refine(
    () => {
      // Default to US validation if we can't determine country context
      // The actual validation will happen in the context of the full form
      return true;
    },
    {
      message: "Please enter a valid postal code",
    },
  ),
  address: z.string().optional(),
  dateOfBirth: z
    .string()
    .min(1, "Date of birth is required")
    .refine(validateDateOfBirth, {
      message: "Please enter a valid date of birth (MM/DD/YYYY)",
    }),
  phone: z.string().min(1, "Phone number is required"),
  phoneValid: z.boolean(),
  ethnicity: z.array(z.string()).optional(),
  termsAccepted: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
  phoneCallAccepted: z.boolean().optional(),
  thirdPartyContactConsentGranted: z.boolean().optional(),
  thirdPartyContactConsentVersion: z.string(),
  consentLocaleCode: z.string(),
  trialSiteId: z.string(),
  trialSiteTrialId: z.string(),
  emrConsentAccepted: z.boolean().optional(),
});

export const existingPatientContactFormSchema = sharedContactFormSchema
  .extend({
    sex: z.enum(["M", "F"], {
      required_error: "Please select your biological sex",
    }).optional(),
    firstName: z.string().min(1, "First name is required").optional(),
    lastName: z.string().min(1, "Last name is required").optional(),
  })
  .refine(...refinePostalCode)
  .refine(...refinePhoneValidation)
  .refine(...refineThirdPartyContactConsent);

export type ExistingPatientContactFormSchema = z.infer<
  typeof existingPatientContactFormSchema
>;

export const newPatientContactFormSchema = sharedContactFormSchema
  .extend({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Please enter a valid email address"),
    sex: z.enum(["M", "F"], {
      required_error: "Please select your biological sex",
    }),
  })
  .refine(...refinePostalCode)
  .refine(...refinePhoneValidation)
  .refine(...refineThirdPartyContactConsent);

export type NewPatientContactFormSchema = z.infer<
  typeof newPatientContactFormSchema
>;

export type ContactFormSchema =
  | ExistingPatientContactFormSchema
  | NewPatientContactFormSchema;

export function isNewPatientFormSchema(
  formData: ContactFormSchema,
): formData is NewPatientContactFormSchema {
  return "email" in formData;
}
